<script>
import bem from '@/utils/bem';
import { mapActions, mapState } from 'vuex';
import { LeftOutlined, RightOutlined } from '@ant-design/icons-vue';
import PageCollection from '@/components/collections/PageCollection.vue';
import NewCollection from '@/components/modals/New-collection.vue';
import NewPageKeyFolder from '@/components/modals/New-page-key-folder.vue';
import notificationWithIcon from '@/utils/notification';
import UploadImage from '@/components/ux/upload/Upload-image.vue';

const DEFAULT_BUTTONS = [
  { key: 'photo_art', text: 'Create' },
  { key: 'photo_restoration', text: 'Restoration' },
  { key: 'proof', text: 'Proof' },
  { key: 'pm_cart', text: 'Pm cart' },
  { key: 'printed_story', text: 'Printed story' },
  { key: 'print_on_demand', text: 'Print on Demand' },
];

const b = bem('collection-page');
export default {
  props: {
    companyId: {
      type: [String, Number],
      default: null,
    },
    viewMode: {
      type: String,
      default: 'all', // 'all', 'collections', 'folders'
    },
  },
  components: {
    UploadImage,
    NewCollection,
    LeftOutlined,
    RightOutlined,
    PageCollection,
    NewPageKeyFolder,
  },
  data() {
    return {
      activeKey: 'photo_art',
      selectedCollection: {},
      topButtons: DEFAULT_BUTTONS,
      companyButtons: [],
      show: false,
      isNew: false,
      showFolder: false,
      selectedFolder: {},
      folders: [],
      showFolders: this.viewMode === 'folders',
      DEFAULT_BUTTONS,
      temporaryImages: [],
      isUploading: false,
      uploadedUids: new Set(),
    };
  },
  methods: {
    b(firstArg, secondArg) {
      return b(firstArg, secondArg);
    },
    ...mapActions({
      getTemplateCollectionsAction: 'collections/getCollections',
      deleteCollectionAction: 'collections/deleteCollection',
      getPagesListAction: 'collections/getPagesList',
      updateCollectionsPositionsAction: 'collections/updateCollectionsPositions',
      updateCollectionDetailsAction: 'collections/updateCollectionDetails',
      getCollectionFoldersAction: 'pageContent/getCollectionFolders',
      uploadPageContentFolderImagesAction: 'pageContent/uploadPageContentFolderImages',
      editSlideAction: 'pageContent/editSlide',
      deleteFolderImageAction: 'pageContent/deleteFolderImage',
      deleteFolderAction: 'pageContent/deleteFolder',
      editPageContentMultiFoldersAction: 'pageContent/editPageContentMultiFolders',
      editPageContentAction: 'pageContent/editPageContent',
      getCompaniesAction: 'companies/getCompanies',
    }),
    selectCollection(value) {
      if (value) {
        this.selectedCollection = value;
      } else {
        this.selectedCollection = {};
        this.getTemplateCollectionsAction({ pageKey: this.activeKey });
      }
    },
    updateCollectionOrder(value) {
      this.updateCollectionsPositionsAction({
        pageKey: this.activeKey,
        collections: value,
      });
    },
    onDelete(value) {
      this.deleteCollectionAction({
        name: value.name,
        pageKey: this.activeKey,
        id: value.id,
      });
    },
    selectType(value) {
      this.activeKey = value;
      this.selectedFolder = {};
      this.uploadedUids.clear();
      this.temporaryImages = [];
      this.getTemplateCollectionsAction({ pageKey: value });
      this.getCollectionFoldersAction(value);
    },
    openNewCollectionPopup() {
      this.show = true;
    },
    closeNewCollectionPopup() {
      this.show = false;
    },
    copyLinkToClipboard(value) {
      const { key, text } = value;
      navigator.clipboard.writeText(`https://foreverstudios.com/pod/?pageKey=${key}`)
        .then(() => {
          notificationWithIcon('success', `${text} link copied!`);
        })
        .catch(() => {
          notificationWithIcon('error', 'Error copying link');
        });
    },
    copyCompanyLinkToClipboard(value) {
      const { key, text } = value;
      navigator.clipboard.writeText(`https://foreverstudios.com/pod/${key}`)
        .then(() => {
          notificationWithIcon('success', `${text} link copied!`);
        })
        .catch(() => {
          notificationWithIcon('error', 'Error copying link');
        });
    },
    openFolderPopUp() {
      this.selectedFolder = {};
      this.showFolder = true;
    },
    closeFolderPopUp() {
      this.showFolder = false;
    },
    toggleFolder(value) {
      // Clear uploaded UIDs when changing folder
      this.uploadedUids.clear();
      this.temporaryImages = [];
      if (this.selectedFolder.id === value.id) {
        this.selectedFolder = {};
      } else {
        this.selectedFolder = {
          ...value,
          blocks: value.blocks.sort((a, c) => a.order - c.order),
        };
      }
    },
    toggleShowFolders() {
      this.showFolders = !this.showFolders;
    },
    uploadNewImages(value) {
      if (value) {
        this.temporaryImages = value;
      }
    },
    saveImageName(block) {
      this.editSlideAction({
        id: block.id,
        goldText: block.goldText,
        goldTextMobile: block.goldTextMobile,
        page: this.activeKey,
        notifyText: 'Image name saved',
      });
    },
    editFolder(value) {
      this.selectedFolder = {
        ...value,
        blocks: value.blocks.sort((a, c) => a.order - c.order),
      };
      this.showFolder = true;
    },
    savePageTexts() {
      this.editPageContentMultiFoldersAction({
        contents: this.folders.map((item) => ({
          id: item.id,
          goldText: this.folders[0].goldText,
          beforeText: this.folders[0].beforeText,
          goldTextMobile: this.folders[0].goldTextMobile,
          afterText: this.folders[0].afterText,
        })),
        page: this.activeKey,
      });
    },
    folderIndexUpdate(currentIndex, newIndex) {
      const currentFolder = this.folders[currentIndex];
      const anotherFolder = this.folders[newIndex];
      const currentPayload = {
        id: currentFolder.id,
        page: this.activeKey,
        params: {
          ...currentFolder,
          index: newIndex,
        },
      };
      const anotherPayload = {
        id: anotherFolder.id,
        page: this.activeKey,
        params: {
          ...anotherFolder,
          index: currentIndex,
        },
      };
      delete currentPayload.params.id;
      delete currentPayload.params.blocks;
      delete anotherPayload.params.id;
      delete anotherPayload.params.blocks;
      this.editPageContentAction(currentPayload);
      this.editPageContentAction(anotherPayload);
    },
    editImagePosition(currentBlock, replacedBlock, currentIndex, replacedIndex) {
      this.editSlideAction({
        id: currentBlock.id,
        order: replacedBlock.index || replacedIndex,
        page: this.activeKey,
        notifyText: 'Image position saved',
      });
      this.editSlideAction({
        id: replacedBlock.id,
        order: currentBlock.index || currentIndex,
        page: this.activeKey,
        notifyText: 'Image position saved',
      });
    },
    initializeForCompany() {
      if (this.companyId && this.companies.length > 0) {
        const company = this.companies.find((c) => String(c.id) === String(this.companyId));
        if (company && company.pageKey) {
          this.activeKey = company.pageKey;
          this.selectedFolder = {};
          this.uploadedUids.clear();
          this.temporaryImages = [];
          this.getTemplateCollectionsAction({ pageKey: this.activeKey });
          this.getCollectionFoldersAction(this.activeKey);
        }
      }
    },
    parseCustomInfo(customInfo) {
      if (!customInfo) return {};
      if (typeof customInfo === 'string') {
        try {
          return JSON.parse(customInfo);
        } catch {
          return {};
        }
      }
      return customInfo;
    },
    isInEnhanceWizard(collection) {
      const customInfo = this.parseCustomInfo(collection.customInfo);
      return customInfo.wizards?.isEnhanceWizard === true;
    },
    isInCreateWizard(collection) {
      const customInfo = this.parseCustomInfo(collection.customInfo);
      return customInfo.wizards?.isCreateWizard === true;
    },
    addToCreateWizard(collection) {
      const existingCustomInfo = this.parseCustomInfo(collection.customInfo);
      const existingWizards = existingCustomInfo.wizards || {};
      const updatedCustomInfo = {
        ...existingCustomInfo,
        wizards: {
          ...existingWizards,
          isCreateWizard: true,
        },
      };
      this.updateCollectionDetailsAction({
        id: collection.id,
        value: {
          customInfo: JSON.stringify(updatedCustomInfo),
        },
        pageKey: this.activeKey,
      });
    },
    removeFromCreateWizard(collection) {
      const existingCustomInfo = this.parseCustomInfo(collection.customInfo);
      const existingWizards = existingCustomInfo.wizards || {};
      const updatedCustomInfo = {
        ...existingCustomInfo,
        wizards: {
          ...existingWizards,
          isCreateWizard: false,
        },
      };
      this.updateCollectionDetailsAction({
        id: collection.id,
        value: {
          customInfo: JSON.stringify(updatedCustomInfo),
        },
        pageKey: this.activeKey,
      });
    },
    addToEnhanceWizard(collection) {
      if (this.enhanceWizardCollections.length > 0) {
        notificationWithIcon(
          'error',
          'Only one collection can be in Enhance wizard. Remove the current one first.',
        );
        return;
      }
      const existingCustomInfo = this.parseCustomInfo(collection.customInfo);
      const existingWizards = existingCustomInfo.wizards || {};
      const updatedCustomInfo = {
        ...existingCustomInfo,
        wizards: {
          ...existingWizards,
          isEnhanceWizard: true,
        },
      };
      this.updateCollectionDetailsAction({
        id: collection.id,
        value: {
          customInfo: JSON.stringify(updatedCustomInfo),
        },
        pageKey: this.activeKey,
      });
    },
    removeFromEnhanceWizard(collection) {
      const existingCustomInfo = this.parseCustomInfo(collection.customInfo);
      const existingWizards = existingCustomInfo.wizards || {};
      const updatedCustomInfo = {
        ...existingCustomInfo,
        wizards: {
          ...existingWizards,
          isEnhanceWizard: false,
        },
      };
      this.updateCollectionDetailsAction({
        id: collection.id,
        value: {
          customInfo: JSON.stringify(updatedCustomInfo),
        },
        pageKey: this.activeKey,
      });
    },
  },
  computed: {
    ...mapState({
      collections: (state) => state.collections.collections,
      collection: (state) => state.collections.collection,
      pageList: (state) => state.collections.collectionsPages,
      collectionFolders: (state) => state.pageContent.collectionFolders,
      companies: (state) => state.companies.companies,
    }),
    filteredTopButtons() {
      const companyPageKeys = this.companyButtons.map((btn) => btn.key);
      return this.topButtons.filter((btn) => !companyPageKeys.includes(btn.key));
    },
    noWizardCollections() {
      return this.collections.filter((collection) => {
        const customInfo = this.parseCustomInfo(collection.customInfo);
        if (!customInfo.wizards) return true;
        const { isCreateWizard, isEnhanceWizard } = customInfo.wizards;
        return !isCreateWizard && !isEnhanceWizard;
      });
    },
    allCollectionsSorted() {
      const noWizard = this.noWizardCollections;
      const createWizard = this.createWizardCollections;
      const enhanceWizard = this.enhanceWizardCollections;
      return [...noWizard, ...createWizard, ...enhanceWizard];
    },
    createWizardCollections() {
      return this.collections.filter((collection) => {
        const customInfo = this.parseCustomInfo(collection.customInfo);
        return customInfo.wizards?.isCreateWizard === true;
      });
    },
    enhanceWizardCollections() {
      return this.collections.filter((collection) => {
        const customInfo = this.parseCustomInfo(collection.customInfo);
        return customInfo.wizards?.isEnhanceWizard === true;
      });
    },
  },
  async mounted() {
    await this.getCompaniesAction({ limit: 1000, offset: 0 });
    if (this.companyId) {
      this.initializeForCompany();
    } else {
      this.getTemplateCollectionsAction({ pageKey: this.activeKey });
      this.getPagesListAction();
      this.getCollectionFoldersAction(this.activeKey);
    }
  },
  watch: {
    pageList: {
      handler(newValue) {
        this.topButtons = newValue.reduce((acc, current) => {
          if (acc.some((item) => item.key === current)) {
            return acc;
          }
          return [
            ...acc,
            {
              text: `${current.replaceAll('_', ' ')}`,
              key: current,
            },
          ];
        }, DEFAULT_BUTTONS);
      },
      deep: true,
    },
    companies: {
      handler(newValue) {
        this.companyButtons = newValue
          .filter((company) => company.pageKey)
          .map((company) => ({
            text: company.displayName,
            key: company.pageKey,
          }));
        if (this.companyId) {
          this.initializeForCompany();
        }
      },
      deep: true,
    },
    collectionFolders: {
      handler(newValue) {
        this.folders = newValue
          .filter((item) => item.name === 'folder')
          .sort((a, c) => a.index - c.index);

        if (this.selectedFolder) {
          const newVal = newValue.find((item) => item.id === this.selectedFolder.id) || {};
          this.selectedFolder = newVal.blocks ? {
            ...newVal,
            blocks: newVal.blocks.sort((a, c) => a.order - c.order),
          } : newVal;
        }
      },
      deep: true,
    },
    temporaryImages: {
      async handler(newValue) {
        if (!newValue || !newValue.length || this.isUploading) {
          return;
        }
        // Filter only new images that haven't been uploaded yet
        const newImagesToUpload = newValue.filter(
          (item) => item.file && item.uid && !this.uploadedUids.has(item.uid),
        );
        if (newImagesToUpload.length === 0) {
          return;
        }
        this.isUploading = true;
        // Mark these UIDs as being uploaded
        newImagesToUpload.forEach((item) => this.uploadedUids.add(item.uid));
        try {
          await this.uploadPageContentFolderImagesAction({
            newImages: newImagesToUpload,
            page: this.activeKey,
            contentId: this.selectedFolder.id,
            order: this.selectedFolder.blocks.length,
          });
        } finally {
          this.isUploading = false;
          this.temporaryImages = [];
        }
      },
      deep: true,
    },
    companyId() {
      this.initializeForCompany();
    },
    viewMode(newVal) {
      if (newVal === 'folders') {
        this.showFolders = true;
      } else if (newVal === 'collections') {
        this.showFolders = false;
      }
    },
  },
};
</script>

<template>
  <div :class="b()" v-if="Object.keys(selectedCollection).length === 0">
    <div :class="b('buttons-container')" v-if="!companyId">
      <div :class="b('buttons-section')">
        <div :class="b('section-title')">
          Page Keys
        </div>
        <div :class="(b('top-buttons'))">
          <div
              :class="b('top-btn-wrap')"
              v-for="item in filteredTopButtons"
              :key="item.key"
          >
            <button :class="b('link-btn')" @click="() => {copyLinkToClipboard(item)}">
              Get link
            </button>
            <a-button
                :class="b('top-btn', { last: item.key === 'print_on_demand' })"
                :type="activeKey === item.key ? 'primary' : 'default'"
                @click="selectType(item.key)"
            >
              {{ item.text }}
            </a-button>
          </div>
        </div>
      </div>
      <div :class="b('buttons-section')" v-if="companyButtons.length > 0">
        <div :class="b('section-title')">
          Companies
        </div>
        <div :class="(b('top-buttons'))">
          <div
              :class="b('top-btn-wrap')"
              v-for="item in companyButtons"
              :key="item.key"
          >
            <button :class="b('link-btn')" @click="() => {copyCompanyLinkToClipboard(item)}">
              Get link
            </button>
            <a-button
                :class="b('top-btn')"
                :type="activeKey === item.key ? 'primary' : 'default'"
                @click="selectType(item.key)"
            >
              {{ item.text }}
            </a-button>
          </div>
        </div>
      </div>
    </div>
    <a-button
        :class="b('new-folder-btn')"
        @click="toggleShowFolders"
        type="primary"
        v-if="viewMode === 'all'"
    >
      {{showFolders ? 'Switch to collection' : 'Switch to folders'}}
    </a-button>
    <template v-if="showFolders">
      <div :class="b('folders-top-texts-wrap')" v-if="folders && folders.length > 0">
        <div :class="b('folders-top-texts')">
          <div :class="b('folders-top-texts-title')">Desktop</div>
          <div :class="b('folders-top-input-wrap')">
            <div :class="b('folders-input-title')">Title:</div>
            <a-input
                style="width: 400px"
                type="text"
                v-model:value="folders[0].goldText"
            />
          </div>
          <div :class="b('folders-top-input-wrap')">
            <div :class="b('folders-input-title')">Description:</div>
            <a-textarea
                style="width: 400px"
                v-model:value="folders[0].beforeText"
                :rows="8"
            />
          </div>
        </div>
        <div :class="b('folders-top-texts')">
          <div :class="b('folders-top-texts-title')">Mobile</div>
          <div :class="b('folders-top-input-wrap')">
            <div :class="b('folders-input-title')">Title:</div>
            <a-input
                style="width: 400px"
                type="text"
                v-model:value="folders[0].goldTextMobile"
            />
          </div>
          <div :class="b('folders-top-input-wrap')">
            <div :class="b('folders-input-title')">Description:</div>
            <a-textarea
                style="width: 400px"
                v-model:value="folders[0].afterText"
                :rows="8"
            />
          </div>
        </div>
        <a-button
            :class="b('save-text-btn')"
            @click="savePageTexts"
            type="primary"
        >
          Save page texts
        </a-button>
      </div>
      <div :class="b('folders')">
        <button
            :class="b('folder', { selected: folder.id === this.selectedFolder.id })"
            v-for="(folder, index) in folders"
            :key="folder.id"
            @click="() => {
              toggleFolder(folder)
            }"
        >
          <img :class="b('folder-preview-image')" :src="folder.imageUrl" alt="folder" />
          <a-button
            :class="b('edit-folder-btn')"
            type="primary"
            size="small"
            html-type="button"
            @click.stop="editFolder(folder)"
          >
            Edit
          </a-button>
          <a-button
            :class="b('delete-folder-btn')"
            type="primary"
            size="small"
            html-type="button"
            danger
            @click.stop="deleteFolderAction({ id: folder.id, page: activeKey })"
          >
            Delete
          </a-button>
          <span :class="b('folder-title')" >{{folder.title}}</span>
          <span :class="b('folder-description')" >{{folder.description}}</span>
          <span
            :class="b('folder-button', { selected: folder.id === this.selectedFolder.id })"
          >{{folder.id === this.selectedFolder.id ? 'SELECTED': 'SELECT'}}</span>
          <div :class="b('order-buttons')">
            <div :class="b('button', { right: true })" v-if="index > 0">
              <a-button
                type="primary"
                size="small"
                @click.stop="folderIndexUpdate(index, index - 1)"
              >
                <template #icon>
                  <LeftOutlined />
                </template>
              </a-button>
            </div>
            <div
              :class="b('button', { left: true })"
              v-if="index !== folders.length - 1"
            >
              <a-button
                type="primary"
                size="small"
                @click.stop="folderIndexUpdate(index, index + 1)"
              >
                <template #icon>
                  <RightOutlined />
                </template>
              </a-button>
            </div>
          </div>
        </button>
        <a-button
            :class="b('new-folder-btn')"
            type="primary"
            @click="openFolderPopUp"
        >
          Create folder
        </a-button>
      </div>
      <div
          :class="b('folder-images')"
          v-if="selectedFolder.id"
      >
        <template v-if="selectedFolder.blocks.length > 0">
          <div
            :class="b('folder-image-wrap')"
            v-for="(block, index) in selectedFolder.blocks"
            v-bind:key="block.id"
          >
            <a-button
                :class="b('delete-folder-image-btn')"
                type="primary"
                size="small"
                html-type="button"
                danger
                @click="() => {
                    deleteFolderImageAction({ id: block.id, page: activeKey });
                  }"
            >
              Delete
            </a-button>
            <div :class="b('folder-image-block')">
              <div :class="b('order-buttons')">
                <div
                    :class="b('position-button', { left: true })"
                    v-if="index !== 0"
                >
                  <a-button
                      type="primary"
                      html-type="button"
                      @click="editImagePosition(
                      block,
                      selectedFolder.blocks[index - 1],
                      index,
                      index - 1
                      )"
                  >
                    <template #icon>
                      <LeftOutlined />
                    </template>
                  </a-button>
                </div>
                <div
                    :class="b('position-button', { right: true })"
                     v-if="index !== selectedFolder.blocks.length - 1"
                >
                  <a-button
                      type="primary"
                      html-type="button"
                      @click="editImagePosition(
                      block,
                      selectedFolder.blocks[index + 1],
                      index,
                      index + 1
                      )"
                  >
                    <template #icon>
                      <RightOutlined />
                    </template>
                  </a-button>
                </div>
              </div>
              <img :class="b('folder-image')" :src="block.imageUrl" alt="folder image">
            </div>
            <div :class="b('folder-text-wrap')">
              <a-input
                  placeholder="Image name"
                  style="max-width: 250px"
                  v-model:value="block.goldText"
              >
              </a-input>
              <a-button
                  :class="b('new-folder-btn')"
                  type="primary"
                  @click="() => {
                    saveImageName(block)
                  }"
              >
                Save
              </a-button>
            </div>
            <a-textarea
                placeholder="Image description"
                style="width: 348px"
                v-model:value="block.goldTextMobile"
                :rows="8"
            />
          </div>
        </template>
        <upload-image
            v-model:files="temporaryImages"
            :is-multiple="true"
            :initial-images="temporaryImages"
            :is-show-upload-list="false"
        />
      </div>
    </template>
    <template v-if="!showFolders">
      <div :class="b('wizard-rows')">
        <!-- All Collections Row (sorted: no wizard -> create -> enhance) -->
        <div :class="b('wizard-row')">
          <div :class="b('wizard-row-title')">Collections</div>
          <div :class="b('wrapper')">
            <div
              :class="b('material')"
              v-for="(collection, index) in allCollectionsSorted"
              :key="'all-' + collection.id"
            >
              <span :class="b('collection')">
                <span :class="b('collection-title')">
                  {{ collection.name }}
                </span>
                <span :class="b('controls')">
                  <a-button
                    :class="b('btn')"
                    type="primary"
                    size="small"
                    @click.stop="selectCollection(collection)"
                  >
                    Edit
                  </a-button>
                  <a-popconfirm
                    :class="b('btn')"
                    title="Sure to delete?"
                    @confirm.stop="onDelete(collection)"
                  >
                    <a-button type="primary" size="small" html-type="button" danger>
                      Delete
                    </a-button>
                  </a-popconfirm>
                </span>
                <span v-if="!companyId" :class="b('order-buttons')">
                  <span :class="b('button', { left: true })" v-if="index > 0">
                    <a-button
                      type="primary"
                      size="small"
                      :class="b('arrow')"
                      @click.stop="updateCollectionOrder([
                        {id: collection.id, index: index - 1},
                        {id: allCollectionsSorted[index - 1].id, index},
                      ])"
                    >
                      <template #icon>
                        <LeftOutlined />
                      </template>
                    </a-button>
                  </span>
                  <span
                    :class="b('button', { right: true })"
                    v-if="index !== allCollectionsSorted.length - 1"
                  >
                    <a-button
                      type="primary"
                      size="small"
                      :class="b('arrow')"
                      @click.stop="updateCollectionOrder([
                        {id: collection.id, index: index + 1},
                        {id: allCollectionsSorted[index + 1].id, index},
                      ])"
                    >
                      <template #icon>
                        <RightOutlined />
                      </template>
                    </a-button>
                  </span>
                </span>
                <img
                  :class="b('image')"
                  :src="collection.previewImage"
                  :alt="collection.name"
                />
                <span :class="b('collection-title')">{{ collection.title }}</span>
                <span :class="b('collection-title')">{{ collection.description }}</span>
                <div :class="b('wizard-buttons')">
                  <a-button
                    v-if="!isInCreateWizard(collection)"
                    :class="b('wizard-btn')"
                    size="small"
                    @click.stop="addToCreateWizard(collection)"
                  >
                    Add to Create
                  </a-button>
                  <a-button
                    v-else
                    :class="b('wizard-btn')"
                    size="small"
                    danger
                    @click.stop="removeFromCreateWizard(collection)"
                  >
                    Remove from Create
                  </a-button>
                  <a-button
                    v-if="!isInEnhanceWizard(collection)"
                    :class="b('wizard-btn')"
                    size="small"
                    @click.stop="addToEnhanceWizard(collection)"
                  >
                    Add to Enhance
                  </a-button>
                  <a-button
                    v-else
                    :class="b('wizard-btn')"
                    size="small"
                    danger
                    @click.stop="removeFromEnhanceWizard(collection)"
                  >
                    Remove from Enhance
                  </a-button>
                </div>
              </span>
            </div>
            <div v-if="allCollectionsSorted.length === 0" :class="b('empty-row')">
              No collections
            </div>
          </div>
        </div>
        <!-- Create Wizard Column -->
        <div :class="b('wizard-row')">
          <div :class="b('wizard-row-title')">Create wizard</div>
          <div :class="b('wrapper')">
            <div
              :class="b('material')"
              v-for="(collection, index) in createWizardCollections"
              :key="'create-' + collection.id"
            >
              <span :class="b('collection')">
                <span :class="b('collection-title')">
                  {{ collection.name }}
                </span>
                <span :class="b('controls')">
                  <a-button
                    :class="b('btn')"
                    type="primary"
                    size="small"
                    @click.stop="selectCollection(collection)"
                  >
                    Edit
                  </a-button>
                  <a-popconfirm
                    :class="b('btn')"
                    title="Sure to delete?"
                    @confirm.stop="onDelete(collection)"
                  >
                    <a-button type="primary" size="small" html-type="button" danger>
                      Delete
                    </a-button>
                  </a-popconfirm>
                </span>
                <span v-if="!companyId" :class="b('order-buttons')">
                  <span :class="b('button', { left: true })" v-if="index > 0">
                    <a-button
                      type="primary"
                      size="small"
                      :class="b('arrow')"
                      @click.stop="updateCollectionOrder([
                        {id: collection.id, index: index - 1},
                        {id: createWizardCollections[index - 1].id, index},
                      ])"
                    >
                      <template #icon>
                        <LeftOutlined />
                      </template>
                    </a-button>
                  </span>
                  <span
                    :class="b('button', { right: true })"
                    v-if="index !== createWizardCollections.length - 1"
                  >
                    <a-button
                      type="primary"
                      size="small"
                      :class="b('arrow')"
                      @click.stop="updateCollectionOrder([
                        {id: collection.id, index: index + 1},
                        {id: createWizardCollections[index + 1].id, index},
                      ])"
                    >
                      <template #icon>
                        <RightOutlined />
                      </template>
                    </a-button>
                  </span>
                </span>
                <img
                  :class="b('image')"
                  :src="collection.previewImage"
                  :alt="collection.name"
                />
                <span :class="b('collection-title')">{{ collection.title }}</span>
                <span :class="b('collection-title')">{{ collection.description }}</span>
                <div :class="b('wizard-buttons')">
                  <a-button
                    :class="b('wizard-btn')"
                    size="small"
                    danger
                    @click.stop="removeFromCreateWizard(collection)"
                  >
                    Remove from Create
                  </a-button>
                  <a-button
                    v-if="enhanceWizardCollections.length === 0 && !isInEnhanceWizard(collection)"
                    :class="b('wizard-btn')"
                    size="small"
                    @click.stop="addToEnhanceWizard(collection)"
                  >
                    Add to Enhance
                  </a-button>
                </div>
              </span>
            </div>
            <div v-if="createWizardCollections.length === 0" :class="b('empty-row')">
              No collections in Create wizard
            </div>
          </div>
        </div>
        <!-- Enhance Wizard Column -->
        <div :class="b('wizard-row')">
          <div :class="b('wizard-row-title')">Enhance wizard</div>
          <div :class="b('wrapper')">
            <div
              :class="b('material')"
              v-for="collection in enhanceWizardCollections"
              :key="'enhance-' + collection.id"
            >
              <span :class="b('collection')">
                <span :class="b('collection-title')">
                  {{ collection.name }}
                </span>
                <span :class="b('controls')">
                  <a-button
                    :class="b('btn')"
                    type="primary"
                    size="small"
                    @click.stop="selectCollection(collection)"
                  >
                    Edit
                  </a-button>
                  <a-popconfirm
                    :class="b('btn')"
                    title="Sure to delete?"
                    @confirm.stop="onDelete(collection)"
                  >
                    <a-button type="primary" size="small" html-type="button" danger>
                      Delete
                    </a-button>
                  </a-popconfirm>
                </span>
                <img
                  :class="b('image')"
                  :src="collection.previewImage"
                  :alt="collection.name"
                />
                <span :class="b('collection-title')">{{ collection.title }}</span>
                <span :class="b('collection-title')">{{ collection.description }}</span>
                <div :class="b('wizard-buttons')">
                  <a-button
                    :class="b('wizard-btn')"
                    size="small"
                    danger
                    @click.stop="removeFromEnhanceWizard(collection)"
                  >
                    Remove from Enhance
                  </a-button>
                  <a-button
                    v-if="!isInCreateWizard(collection)"
                    :class="b('wizard-btn')"
                    size="small"
                    @click.stop="addToCreateWizard(collection)"
                  >
                    Add to Create
                  </a-button>
                </div>
              </span>
            </div>
            <div v-if="enhanceWizardCollections.length === 0" :class="b('empty-row')">
              No collection selected for Enhance wizard
            </div>
          </div>
        </div>
      </div>
      <a-button
        :class="b('new-collection-btn')"
        type="primary"
        @click.stop="openNewCollectionPopup"
        v-if="!companyId"
      >
        Create collection
      </a-button>
    </template>
    <NewCollection
        v-model:page-key="activeKey"
        :hide-modal="closeNewCollectionPopup"
        :show="show"
        :pageList="topButtons"
    />
    <NewPageKeyFolder
        :show="showFolder"
        :hide-modal="closeFolderPopUp"
        :page-key="activeKey"
        :selected-folder="selectedFolder"
        :index="folders.length + 1"
    />
  </div>
  <PageCollection
    v-else
    :selected-collection="selectedCollection"
    :go-back="selectCollection"
    :company-id="companyId"
    :view-mode="viewMode"
  />
</template>

<style src="./style.scss" scoped lang="scss"></style>
